import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Building2, Search, MapPin, Package, FileText, Loader2, AlertCircle, Check } from 'lucide-react';
import { GET_LABORATORIES } from '../../api/laboratory-api';

const Step1LabPicker = ({ selectedLaboratory, onLaboratorySelect }) => {
  const [search, setSearch] = useState('');

  const { loading, error, data, refetch } = useQuery(GET_LABORATORIES, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const labs = useMemo(() => {
    if (!data?.laboratories) return [];
    if (!search) return data.laboratories;
    const q = search.toLowerCase();
    return data.laboratories.filter(
      lab =>
        lab.nom?.toLowerCase().includes(q) ||
        lab.raisonSociale?.toLowerCase().includes(q) ||
        lab.adresse?.toLowerCase().includes(q)
    );
  }, [data, search]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-7 w-7 text-blue-500 animate-spin mb-3" />
        <p className="text-sm text-gray-400">Chargement des laboratoires...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="h-9 w-9 text-red-400 mb-3" />
        <p className="text-sm text-gray-600 mb-4">Impossible de charger les laboratoires</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const allLabs = data?.laboratories || [];

  if (allLabs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Building2 className="h-9 w-9 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">Aucun laboratoire enregistré</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-5 flex-shrink-0">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Choisir un laboratoire</h2>
        <p className="text-sm text-gray-500">
          Sélectionnez le laboratoire partenaire pour ce contrat
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-3 flex-shrink-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher par nom, raison sociale, adresse..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
          >
            ✕
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400 mb-3 flex-shrink-0">
        {labs.length} laboratoire{labs.length !== 1 ? 's' : ''}
        {search ? ` pour « ${search} »` : ''}
      </p>

      {/* List */}
      {labs.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400">
          Aucun résultat pour « {search} »
        </div>
      ) : (
        <div className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-0.5">
          {labs.map(lab => {
            const isSelected = selectedLaboratory?.id === lab.id;
            const initials = (lab.nom || '??').slice(0, 2).toUpperCase();

            return (
              <button
                key={lab.id}
                onClick={() => onLaboratorySelect(lab)}
                className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-100 hover:border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                {/* Avatar */}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                    isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {initials}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{lab.nom}</p>
                  {lab.raisonSociale && (
                    <p className="text-xs text-gray-400 truncate">{lab.raisonSociale}</p>
                  )}
                  {lab.adresse && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                      <MapPin size={11} />
                      {lab.adresse}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-5 flex-shrink-0">
                  <div className="text-center hidden sm:block">
                    <p className="text-sm font-bold text-gray-900">{lab.products?.length || 0}</p>
                    <p className="text-xs text-gray-400">Produits</p>
                  </div>
                  <div className="text-center hidden sm:block">
                    <p className="text-sm font-bold text-gray-900">{lab.contrats?.length || 0}</p>
                    <p className="text-xs text-gray-400">Contrats</p>
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-blue-600' : 'bg-gray-100'
                    }`}
                  >
                    {isSelected ? (
                      <Check size={13} className="text-white" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Step1LabPicker;
